import React, { forwardRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { useRecoilState } from 'recoil';

import { alertsModalPageTestIDs, Theme } from '@constants';
import { useContextNodeHandle, useIndexedStateReference, useNodeHandleFromIndex } from '@hooks';
import { navigationService } from '@services';
import { ldlPlaylistState } from '@store';

import { FocusableSwitch } from '../../focusable/FocusableSwitch/FocusableSwitch';
import { TWCTile } from '../../focusable/TWCTile/TWCTile';
import { ldlPlaylist } from '../../lowerDisplayLine/LDLBase/ldlPlaylist';
import { TWCText } from '../../primitives/TWCText/TWCText';

const numberProducts = ldlPlaylist.length + 3; // include "Severe Crawl ON/OFF" and "Animation ON/OFF"

const onPressAlertOverlay = () => {
  navigationService.navigate(alertsModalPageTestIDs.alertsModalPage, { type: 'alertOverlay' });
};

export const LDLDebug = () => {
  const [stateReferences, , setStateReferences] = useIndexedStateReference({});
  const [playlistState, setPlaylistState] = useRecoilState(ldlPlaylistState);
  const { activeProductIndex, isAnimationEnabled, isSevereCrawlEnabled } = playlistState;

  const handleAnimationToggle = () => {
    const changedValue = !isAnimationEnabled;

    setPlaylistState({ ...playlistState, isAnimationEnabled: changedValue });
  };

  const handleSevereCrawlToggle = () => {
    setPlaylistState({
      ...playlistState,
      isSevereCrawlEnabled: !isSevereCrawlEnabled,
    });
  };

  return (
    <View style={styles.ldlDebug}>
      {ldlPlaylist.map((name, index) => (
        <DebugProduct
          key={name}
          index={index}
          isActiveProduct={index === activeProductIndex}
          productName={name}
          ref={reference => {
            setStateReferences(reference, index);
          }}
          references={stateReferences}
          onPress={() => {
            setPlaylistState({
              ...playlistState,
              activeProductIndex: index,
              nextProductIndex: index,
            });
          }}
        />
      ))}
      <AlertOverlay
        ref={reference => {
          setStateReferences(reference, numberProducts - 3);
        }}
        onPress={onPressAlertOverlay}
      />
      <AnimationSwitch
        isAnimationEnabled={isAnimationEnabled}
        ref={reference => {
          setStateReferences(reference, numberProducts - 2);
        }}
        onValueChange={handleAnimationToggle}
      />
      <SevereCrawlSwitch
        isSevereCrawlEnabled={isSevereCrawlEnabled}
        ref={reference => {
          setStateReferences(reference, numberProducts - 1);
        }}
        onValueChange={handleSevereCrawlToggle}
      />
    </View>
  );
};

const DebugProduct = forwardRef(({ isActiveProduct, productName, index, references, onPress }, reference) => {
  const itemNextHandle = useNodeHandleFromIndex(references, index < numberProducts - 1 ? index + 1 : null);

  const firstHandle = useContextNodeHandle('LDL_alertHeadlines_debug_item');
  const itemPreviousHandle = useNodeHandleFromIndex(references, index > 0 ? index - 1 : null);

  return (
    <TWCTile
      key={productName}
      accessibilityHint={''}
      accessibilityLabel={''}
      backgroundDefault={isActiveProduct ? { backgroundColor: '#119911' } : null}
      height={75}
      nextFocusDown={null}
      nextFocusLeft={index === 1 ? firstHandle : itemPreviousHandle}
      nextFocusRight={itemNextHandle}
      ref={reference}
      shouldUseContextReference={index === 0}
      testID={`LDL_${productName}_debug_item`}
      useLinearGradient={!isActiveProduct}
      width={240}
      onPress={onPress}
    >
      <View style={styles.ldlDebugText}>
        <TWCText noTestID variant={'regularText30'}>
          {productName}
        </TWCText>
      </View>
    </TWCTile>
  );
});

const SevereCrawlSwitch = forwardRef(({ isSevereCrawlEnabled, onValueChange }, reference) => (
  <View style={styles.severeCrawlSwitch}>
    <TWCText noTestID style={styles.severeCrawlSwitchLabel} variant={'regularText30'}>
      {'Severe Crawl'}
    </TWCText>
    <FocusableSwitch
      activeText={'ON'}
      backgroundActive={Theme.switch.activeColor}
      backgroundInactive={Theme.switch.inactiveColor}
      barHeight={Theme.px(66)}
      changeValueImmediately
      circleBorderWidth={0}
      circleSize={Theme.px(54)}
      colorFocused={Theme.switch.focusColor}
      inActiveText={'OFF'}
      nativeID={'LDL_severe_crawl_debug_item'}
      ref={reference}
      renderInsideCircle={() => (
        <TWCText
          noTestID
          style={[
            styles.severeCrawlSwitchText,
            {
              color: isSevereCrawlEnabled ? Theme.severeAlertColor : Theme.white,
            },
          ]}
        >
          {isSevereCrawlEnabled ? '✔' : ' '}
        </TWCText>
      )}
      switchLeftPx={2}
      switchRightPx={2}
      switchWidthMultiplier={3.15}
      testID={'LDL_severe_crawl_debug_item'}
      value={isSevereCrawlEnabled}
      onValueChange={onValueChange}
    />
  </View>
));

const AnimationSwitch = forwardRef(({ isAnimationEnabled, onValueChange }, reference) => (
  <View style={styles.animationSwitch}>
    <TWCText noTestID style={styles.animationSwitchLabel} variant={'regularText30'}>
      {'Animation'}
    </TWCText>
    <FocusableSwitch
      activeText={'ON'}
      backgroundActive={Theme.switch.activeColor}
      backgroundInactive={Theme.switch.inactiveColor}
      barHeight={Theme.px(66)}
      changeValueImmediately
      circleBorderWidth={0}
      circleSize={Theme.px(54)}
      colorFocused={Theme.switch.focusColor}
      inActiveText={'OFF'}
      nativeID={'LDL_animation_debug_item'}
      ref={reference}
      renderInsideCircle={() => (
        <TWCText
          noTestID
          style={[
            styles.animationSwitchText,
            {
              color: isAnimationEnabled ? Theme.pacificBlue : Theme.white,
            },
          ]}
        >
          {isAnimationEnabled ? '✔' : ' '}
        </TWCText>
      )}
      switchLeftPx={2}
      switchRightPx={2}
      switchWidthMultiplier={3.15}
      testID={'LDL_animation_debug_item'}
      value={isAnimationEnabled}
      onValueChange={onValueChange}
    />
  </View>
));

const AlertOverlay = forwardRef(({ onPress }, reference) => (
  <TWCTile
    key={'alertOverlay'}
    accessibilityHint={''}
    accessibilityLabel={''}
    height={75}
    nextFocusDown={null}
    nextFocusLeft={numberProducts.length - 4}
    nextFocusRight={numberProducts.length - 2}
    ref={reference}
    shouldUseContextReference={false}
    testID={'LDL_alertOverlay_debug_item'}
    width={240}
    onPress={onPress}
  >
    <View style={styles.ldlDebugText}>
      <TWCText noTestID variant={'regularText30'}>
        {'alertOverlay'}
      </TWCText>
    </View>
  </TWCTile>
));

const styles = StyleSheet.create({
  animationSwitch: {
    alignItems: 'center',
    backgroundColor: Theme.pacificBlue,
    borderColor: Theme.transparent,
    borderRadius: Theme.px(6),
    flexDirection: 'row',
    height: Theme.px(75),
    justifyContent: 'center',
    marginHorizontal: Theme.px(6),
    marginTop: Theme.px(6),
    width: Theme.px(372),
  },
  animationSwitchLabel: {
    color: Theme.white,
    marginHorizontal: Theme.px(8),
  },
  animationSwitchText: {
    color: Theme.switch.focusColor,
    fontSize: Theme.px(36),
  },
  ldlDebug: {
    bottom: Theme.px(500),
    elevation: 2000,
    flexDirection: 'row',
    flexWrap: 'wrap',
    left: 0,
    position: 'absolute',
    zIndex: 2000,
  },
  ldlDebugText: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
  },
  severeCrawlSwitch: {
    alignItems: 'center',
    backgroundColor: Theme.severeAlertColor,
    borderColor: Theme.transparent,
    borderRadius: Theme.px(6),
    flexDirection: 'row',
    height: Theme.px(75),
    marginLeft: Theme.px(6),
    marginTop: Theme.px(6),
    width: Theme.px(360),
  },
  severeCrawlSwitchLabel: {
    marginHorizontal: Theme.px(8),
  },
  severeCrawlSwitchText: {
    color: Theme.switch.focusColor,
    fontSize: Theme.px(36),
  },
});
